/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { createFeatureStoreHouse } from 'src/luis/core/stores/featureStoreHouse';
import { createUtteranceStoreHouse } from 'src/luis/projects/utterances/stores/utteranceStoreHouse';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { IntentStore } from 'src/luis/types/stores/intents/IntentStore';

/**
 * An intent store factory object.
 *
 * @param id The id of the intent to create.
 * @param intent The intent data.
 */
export const createIntentStore = (id: LuisId, intent: Partial<IntentStore>): IntentStore => {
    const store: IntentStore = {
        id,
        typeId: ModelType.Intent,
        name: intent.name,
        utteranceStoreHouse: createUtteranceStoreHouse({
            pageSize: LuisConstants.ResourceLimits.DefaultPageSize,
            totalSize: intent.labelsCount || 0
        }),
        featureStoreHouse: intent.featureStoreHouse ? intent.featureStoreHouse : createFeatureStoreHouse(),

        /**
         * Computes labels count based on the utterance store house total size.
         */
        get labelsCount() {
            return this.utteranceStoreHouse.totalSize;
        },

        /**
         * Renames the intent with the given name.
         *
         * @param name The name to rename the intent with.
         */
        rename(name: string): void {
            this.name = name;
        }
    };

    return observable<IntentStore>(
        store,
        {
            name: observable,
            labelsCount: computed,
            rename: action
        },
        { deep: false }
    );
};
